import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DemoWidget from '../components/DemoWidget';

// ── Cache Status Explorer ──────────────────────────────────────────
const ROUTES = [
  { path: '/api/route1.shrutidances.com', label: 'Route 1 (static HTML)', expectedCache: 'HIT', ttl: '2h', size: '1.2 KB' },
  { path: '/api/route2.shrutidances.com', label: 'Route 2 (static HTML)', expectedCache: 'HIT', ttl: '2h', size: '1.1 KB' },
  { path: '/api/route3.shrutidances.com', label: 'Route 3 (dynamic)', expectedCache: 'DYNAMIC', ttl: '0s', size: 'N/A' },
  { path: '/', label: 'Homepage', expectedCache: 'REVALIDATED', ttl: '5m', size: '3.4 KB' },
  { path: '/videos', label: '/videos (video content)', expectedCache: 'MISS', ttl: '1m', size: '— (stream)' },
];

const CACHE_STATUS_COLORS: Record<string, string> = {
  HIT: 'var(--cf-success)',
  MISS: 'var(--cf-danger)',
  REVALIDATED: 'var(--cf-warning)',
  DYNAMIC: 'var(--cf-blue)',
  BYPASS: 'var(--cf-muted)',
  EXPIRED: '#92400e',
};

function CacheExplorer() {
  const [selected, setSelected] = useState(0);
  const [fetching, setFetching] = useState(false);
  const [fetched, setFetched] = useState(false);

  const route = ROUTES[selected];

  const fetch = () => {
    setFetching(true);
    setFetched(false);
    setTimeout(() => { setFetching(false); setFetched(true); }, 900);
  };

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        Select a route on <strong>shrutidances.com</strong> and inspect the <code>cf-cache-status</code> header that Cloudflare returns.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
        {ROUTES.map((r, i) => (
          <button
            key={r.path}
            onClick={() => { setSelected(i); setFetched(false); }}
            style={{
              textAlign: 'left', padding: '0.6rem 0.875rem',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${selected === i ? 'var(--cf-orange)' : 'var(--cf-border)'}`,
              background: selected === i ? 'var(--cf-orange-light)' : 'white',
              cursor: 'pointer', transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--cf-mid)' }}>{r.path}</span>
            <span style={{ fontSize: '0.72rem', color: 'var(--cf-muted)' }}>{r.label}</span>
          </button>
        ))}
      </div>

      <button className="btn btn-primary" onClick={fetch} disabled={fetching}>
        {fetching ? <><span className="spinner" /> Fetching...</> : `GET ${route.path}`}
      </button>

      {fetched && (
        <div style={{ marginTop: '1rem' }}>
          <div className="result-box result-info" style={{ marginBottom: '0.75rem' }}>
            <strong>Response Headers</strong>
            <pre style={{ marginTop: '0.625rem', fontSize: '0.8rem' }}>
{`HTTP/2 200 OK
server: cloudflare
cf-cache-status: ${route.expectedCache}
cf-ray: 9d1b3c4f5e6a-SJC
cache-control: max-age=${route.ttl === '0s' ? '0' : route.ttl === '2h' ? '7200' : '300'}
content-length: ${route.size}
age: 142`}
            </pre>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              padding: '0.35rem 0.875rem',
              borderRadius: 'var(--radius-sm)',
              background: `${CACHE_STATUS_COLORS[route.expectedCache]}22`,
              border: `1px solid ${CACHE_STATUS_COLORS[route.expectedCache]}44`,
              color: CACHE_STATUS_COLORS[route.expectedCache],
              fontWeight: 700, fontSize: '0.875rem',
            }}>
              cf-cache-status: {route.expectedCache}
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--cf-muted)' }}>
              {route.expectedCache === 'HIT' && 'Served from Cloudflare edge cache — origin not contacted'}
              {route.expectedCache === 'MISS' && 'Cache miss — request forwarded to origin'}
              {route.expectedCache === 'REVALIDATED' && 'Stale entry revalidated with origin via 304'}
              {route.expectedCache === 'DYNAMIC' && 'Cache-Control: no-store — not cacheable by CF'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Image Optimization ─────────────────────────────────────────────
function ImageOptDemo() {
  const [polish, setPolish] = useState(true);
  const [webp, setWebp] = useState(true);

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1.25rem' }}>
        Cloudflare Polish automatically compresses and converts images at the edge.
        No code changes required — enable in the Speed dashboard tab.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div style={{ background: 'var(--cf-bg)', borderRadius: 'var(--radius-sm)', padding: '1rem', border: '1px solid var(--cf-border)' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--cf-muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Origin JPEG
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--cf-dark)' }}>842 KB</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--cf-muted)', marginTop: '0.25rem' }}>Uncompressed, JPEG 2400×1600</div>
        </div>
        <div style={{ background: 'var(--cf-success-light)', borderRadius: 'var(--radius-sm)', padding: '1rem', border: '1px solid #a7f3d0' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--cf-success)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            After Polish {webp ? '+ WebP' : ''}
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--cf-success)' }}>
            {webp ? (polish ? '128 KB' : '380 KB') : (polish ? '310 KB' : '842 KB')}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--cf-success)', marginTop: '0.25rem' }}>
            {webp ? 'WebP' : 'JPEG'} — {((1 - (webp ? (polish ? 128 : 380) : (polish ? 310 : 842)) / 842) * 100).toFixed(0)}% smaller
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={polish} onChange={() => setPolish(!polish)} />
          <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>Polish (lossless compress)</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={webp} onChange={() => setWebp(!webp)} />
          <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>WebP conversion</span>
        </label>
      </div>

      <div className="result-box result-info" style={{ fontSize: '0.85rem' }}>
        <strong>How it works:</strong> When a browser sends <code>Accept: image/webp</code>, Cloudflare
        serves a converted WebP at the edge. Polish strips EXIF metadata and re-compresses.
        Zero code changes, enabled per-zone in the Speed tab.
      </div>
    </div>
  );
}

// ── Geo Routing ────────────────────────────────────────────────────
function GeoRoutingDemo() {
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState<null | { colo: string; country: string; city: string; asn: string; latency: number; dc: string }>(null);

  const fetch = async () => {
    setFetching(true);
    setData(null);
    // Simulate fetching from a Worker that returns request metadata
    setTimeout(() => {
      setData({
        colo: 'SJC',
        country: 'US',
        city: 'San Jose',
        asn: 'AS7922 Comcast',
        latency: 8,
        dc: 'San Jose, CA — Cloudflare PoP #134',
      });
      setFetching(false);
    }, 1200);
  };

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        Shows the Cloudflare edge datacenter (colo) serving your request, your IP metadata,
        and the latency to the nearest PoP.
      </p>

      <button className="btn btn-primary" onClick={fetch} disabled={fetching} style={{ marginBottom: '1.25rem' }}>
        {fetching ? <><span className="spinner" /> Querying edge...</> : 'Where Am I Being Served From?'}
      </button>

      {data && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            {[
              { label: 'Edge Colo', value: data.colo, sub: data.dc },
              { label: 'Your Country', value: data.country, sub: data.city },
              { label: 'Edge Latency', value: `${data.latency}ms`, sub: 'to nearest PoP' },
            ].map((m) => (
              <div key={m.label} style={{
                background: 'var(--cf-bg)', borderRadius: 'var(--radius-sm)',
                padding: '0.875rem', border: '1px solid var(--cf-border)',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--cf-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>{m.label}</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--cf-orange)' }}>{m.value}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--cf-muted)', marginTop: '0.2rem' }}>{m.sub}</div>
              </div>
            ))}
          </div>
          <div className="result-box result-success" style={{ fontSize: '0.85rem' }}>
            Your request was routed via anycast to the <strong>{data.colo}</strong> Cloudflare datacenter —
            the closest PoP to your IP. This is available in Workers as <code>request.cf.colo</code>.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────
export default function PerformancePage() {
  return (
    <main className="main">
      <div className="demo-page">
        <div className="demo-page-header">
          <div className="breadcrumb">
            <Link to="/">Portal</Link>
            <span className="breadcrumb-sep">/</span>
            <span>Performance</span>
          </div>
          <h2>CDN & Performance Demos</h2>
          <p>
            UI prototypes for planned cache, image optimization, and geographic-routing demonstrations.
            Values shown on this page are illustrative until live product integrations are added.
          </p>
        </div>

        <div className="demo-list">
          <DemoWidget
            icon="📦"
            title="Cache Status Explorer Prototype"
            product="Illustrative"
            script="Use this UI to explain HIT, MISS, and REVALIDATED. The current headers and timing values are illustrative, not live response data."
          >
            <CacheExplorer />
          </DemoWidget>

          <DemoWidget
            icon="🖼️"
            title="Image Optimization Prototype"
            product="Illustrative"
            script="Use the toggles to preview the intended comparison UI. Current file sizes are illustrative until a live image endpoint is connected."
          >
            <ImageOptDemo />
          </DemoWidget>

          <DemoWidget
            icon="🌍"
            title="Geographic Routing Prototype"
            product="Illustrative"
            script="This currently previews the geographic-routing UI with sample values; it does not measure the visitor's live route."
          >
            <GeoRoutingDemo />
          </DemoWidget>
        </div>
      </div>
    </main>
  );
}
